/**
 * MongoDB-style filter for front-end data filtering
 * Applies MongoDB query operators to JavaScript objects
 */

// Type definitions for MongoDB query operators
export interface MongoFilter {
  [key: string]: any;
  $and?: MongoFilter[];
  $or?: MongoFilter[];
  $nor?: MongoFilter[];
  $not?: MongoFilter;
}

/**
 * Gets a value from an object using dot notation or array index
 * Supports nested field access like 'profile.social.twitter' and array indices like 'tags.0'
 * Returns an object with the value, whether the field exists, and whether it's an array index access
 */
export function getValueByPath(obj: any, path: string): { value: any; exists: boolean; isArrayIndex: boolean } {
  if (obj === null || obj === undefined) {
    return { value: undefined, exists: false, isArrayIndex: false };
  }

  const parts = path.split(".");
  let current = obj;
  let isArrayIndex = false;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (part === undefined) {
      return { value: undefined, exists: false, isArrayIndex };
    }

    if (current === null || current === undefined) {
      return { value: undefined, exists: false, isArrayIndex };
    }

    // Handle array index access
    if (Array.isArray(current) && /^\d+$/.test(part)) {
      isArrayIndex = true;
      const index = parseInt(part, 10);
      if (index >= current.length) {
        return { value: undefined, exists: false, isArrayIndex: true };
      }
      current = current[index];
    } else if (typeof current === "object") {
      // For the last part, check if the property actually exists
      if (i === parts.length - 1) {
        const exists = part in current;
        return { value: current[part], exists, isArrayIndex };
      }

      if (!(part in current)) {
        return { value: undefined, exists: false, isArrayIndex };
      }
      current = current[part];
    } else {
      return { value: undefined, exists: false, isArrayIndex };
    }
  }

  return { value: current, exists: true, isArrayIndex };
}

/**
 * Gets the JavaScript type name for MongoDB $type operator
 */
function getMongoType(value: any): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (value instanceof Date) return "date";
  if (value instanceof RegExp) return "regex";
  return typeof value;
}

/**
 * Checks if an array contains a specific value (for array field matching)
 */
function arrayContains(arr: any[], value: any): boolean {
  if (!Array.isArray(arr)) return false;
  return arr.some((item) => {
    // Direct equality check (handles null, undefined, primitives)
    if (item === value) return true;

    // Both are objects (but not null) - use deep equality
    if (typeof item === "object" && typeof value === "object" && item !== null && value !== null) {
      return deepEquals(item, value);
    }

    return false;
  });
}

/**
 * Evaluates comparison operators ($gt, $gte, $lt, $lte, $ne, $in, $nin, etc.)
 */
function evaluateComparison(fieldValue: any, fieldExists: boolean, operator: string, operatorValue: any): boolean {
  switch (operator) {
    case "$eq":
      return fieldValue === operatorValue;

    case "$ne":
      return fieldValue !== operatorValue;

    case "$gt":
      return fieldValue != null && operatorValue != null && fieldValue > operatorValue;

    case "$gte":
      return fieldValue != null && operatorValue != null && fieldValue >= operatorValue;

    case "$lt":
      return fieldValue != null && operatorValue != null && fieldValue < operatorValue;

    case "$lte":
      return fieldValue != null && operatorValue != null && fieldValue <= operatorValue;

    case "$in": {
      if (!Array.isArray(operatorValue)) return false;
      // A null entry in the array matches a missing field, mirroring MongoDB and the direct-null branch.
      if (!fieldExists && operatorValue.includes(null)) return true;
      return operatorValue.includes(fieldValue);
    }

    case "$nin": {
      if (!Array.isArray(operatorValue)) return false;
      if (!fieldExists && operatorValue.includes(null)) return false;
      return !operatorValue.includes(fieldValue);
    }

    case "$exists":
      return operatorValue ? fieldExists : !fieldExists;

    case "$regex": {
      if (fieldValue == null) return false;
      const regex = operatorValue instanceof RegExp ? operatorValue : new RegExp(operatorValue);
      return regex.test(String(fieldValue));
    }

    case "$type":
      return getMongoType(fieldValue) === operatorValue;

    case "$mod": {
      if (!Array.isArray(operatorValue) || operatorValue.length !== 2) return false;
      if (typeof fieldValue !== "number") return false;
      const [divisor, remainder] = operatorValue;
      return fieldValue % divisor === remainder;
    }

    case "$all":
      if (!Array.isArray(fieldValue) || !Array.isArray(operatorValue)) return false;
      return operatorValue.every((value) => arrayContains(fieldValue, value));

    case "$size":
      if (!Array.isArray(fieldValue)) return false;
      if (typeof operatorValue === "number") {
        return fieldValue.length === operatorValue;
      } else if (typeof operatorValue === "object") {
        // Handle $size with operators like { $size: { $gte: 3 } }
        return evaluateFieldCondition(fieldValue.length, true, false, operatorValue);
      }
      return false;

    case "$elemMatch":
      if (!Array.isArray(fieldValue)) return false;
      return fieldValue.some((element) => applyMongoFilter(element, operatorValue));

    case "$not":
      return !evaluateFieldCondition(fieldValue, fieldExists, false, operatorValue);

    default:
      return false;
  }
}

/**
 * Deep equality comparison for objects and arrays (MongoDB-style, order-sensitive for objects)
 */
function deepEquals(a: any, b: any): boolean {
  if (a === b) return true;

  if (a === null || b === null || a === undefined || b === undefined) {
    return a === b;
  }

  if (typeof a !== typeof b) return false;

  if (typeof a === "object") {
    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    // Handle RegExp objects
    if (a instanceof RegExp && b instanceof RegExp) {
      return a.toString() === b.toString();
    }

    // One is Date/RegExp and other is not
    if (a instanceof Date !== b instanceof Date || a instanceof RegExp !== b instanceof RegExp) {
      return false;
    }

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEquals(a[i], b[i])) return false;
      }
      return true;
    }

    // For plain objects, MongoDB is property-order sensitive
    // Use JSON.stringify for order-sensitive comparison
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      // Fallback for objects that can't be stringified (circular refs, etc.)
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!deepEquals(a[key], b[key])) return false;
      }
      return true;
    }
  }

  return false;
}

/**
 * Checks if an object contains MongoDB operators
 */
function isOperatorObject(obj: any): boolean {
  if (typeof obj !== "object" || obj === null) return false;

  const keys = Object.keys(obj);
  return keys.some((key) => key.startsWith("$"));
}

/**
 * Evaluates a single field condition (handles both direct values and operator objects)
 */
function evaluateFieldCondition(fieldValue: any, fieldExists: boolean, isArrayIndex: boolean, condition: any): boolean {
  // Direct value comparison
  if (typeof condition !== "object" || condition === null || Array.isArray(condition)) {
    // If condition is an array, check for exact equality first
    if (Array.isArray(condition)) {
      if (Array.isArray(fieldValue)) {
        return deepEquals(fieldValue, condition);
      }
      return false;
    }

    // If field is array and condition is not an array, check if array contains the value
    if (Array.isArray(fieldValue)) {
      // For null, check both array containment AND the null/missing field logic
      if (condition === null) {
        return arrayContains(fieldValue, null) || fieldValue === null || !fieldExists;
      }
      return arrayContains(fieldValue, condition);
    }

    // In MongoDB, null matches both null values and missing fields (for non-array fields)
    // But for array indices, null should NOT match out-of-bounds indices
    if (condition === null) {
      if (isArrayIndex) {
        // For array indices, only match actual null values, not missing indices
        return fieldValue === null;
      } else {
        // For object properties, match both null values and missing fields
        return fieldValue === null || !fieldExists;
      }
    }

    return fieldValue === condition;
  }

  // If it's an object but not an operator object, do direct object comparison
  if (!isOperatorObject(condition)) {
    return deepEquals(fieldValue, condition);
  }

  // Handle regex with options
  if (condition.$regex !== undefined && condition.$options !== undefined) {
    const flags = condition.$options;
    const regex = new RegExp(condition.$regex, flags);
    return regex.test(String(fieldValue || ""));
  }

  // Handle operator object
  for (const [operator, operatorValue] of Object.entries(condition)) {
    if (!evaluateComparison(fieldValue, fieldExists, operator, operatorValue)) {
      return false;
    }
  }

  return true;
}

/**
 * Applies MongoDB-style filter to a single document
 * @param document - The document to test
 * @param filter - The MongoDB-style filter object
 * @returns boolean - true if document matches filter
 */
export function applyMongoFilter(document: any, filter: MongoFilter): boolean {
  // Validate inputs
  if (document === null || document === undefined) {
    throw new Error("Document cannot be null or undefined");
  }

  if (filter === null || filter === undefined) {
    return false;
  }

  if (typeof filter !== "object" || Array.isArray(filter)) {
    return false;
  }

  // Empty filter matches all documents
  if (Object.keys(filter).length === 0) {
    return true;
  }

  // Handle logical operators (only if they have the correct type)
  if (filter.$and !== undefined && Array.isArray(filter.$and)) {
    return filter.$and.every((subFilter) => applyMongoFilter(document, subFilter));
  }

  if (filter.$or !== undefined && Array.isArray(filter.$or)) {
    if (filter.$or.length === 0) return false;
    return filter.$or.some((subFilter) => applyMongoFilter(document, subFilter));
  }

  if (filter.$nor !== undefined && Array.isArray(filter.$nor)) {
    return !filter.$nor.some((subFilter) => applyMongoFilter(document, subFilter));
  }

  if (filter.$not !== undefined && typeof filter.$not === "object" && filter.$not !== null) {
    return !applyMongoFilter(document, filter.$not);
  }

  // Handle field-level conditions
  for (const [fieldPath, condition] of Object.entries(filter)) {
    // Skip logical operators that were actually processed above
    if (fieldPath === "$and" && Array.isArray(condition)) continue;
    if (fieldPath === "$or" && Array.isArray(condition)) continue;
    if (fieldPath === "$nor" && Array.isArray(condition)) continue;
    if (fieldPath === "$not" && typeof condition === "object" && condition !== null) continue;

    // An `undefined` condition is dropped during request serialization (JSON.stringify omits
    // undefined values), so the backend never filters on it. Mirror that by ignoring the field.
    if (condition === undefined) continue;

    const { value: fieldValue, exists: fieldExists, isArrayIndex } = getValueByPath(document, fieldPath);

    if (!evaluateFieldCondition(fieldValue, fieldExists, isArrayIndex, condition)) {
      return false;
    }
  }

  return true;
}

/**
 * Convenience function to filter an array of documents
 * @param documents - Array of documents to filter
 * @param filter - MongoDB-style filter object
 * @returns Filtered array of documents
 */
export function filterDocuments<T = any>(documents: T[], filter: MongoFilter): T[] {
  if (!Array.isArray(documents)) {
    throw new Error("Documents must be an array");
  }

  return documents.filter((doc) => applyMongoFilter(doc, filter));
}
